import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { getPasswordStrength, PASSWORD_RULES, PASSWORD_STRENGTH_LEVELS, type PasswordRule } from './passwordStrength';
import styles from './PasswordStrengthMeter.module.scss';
import ruleMetIcon from './rule-met.svg';
import ruleMissingIcon from './rule-missing.svg';

interface Props {
  password: string;
  /** Show the requirements checklist while the field is still empty. */
  showHint?: boolean;
  className?: string;
  'data-testid'?: string;
}

const STRENGTH_LABEL_KEYS = {
  weak: 'passwordStrength.weak',
  fair: 'passwordStrength.fair',
  strong: 'passwordStrength.strong',
} as const;

const RULE_LABEL_KEYS: Record<PasswordRule, `passwordStrength.rules.${PasswordRule}`> = {
  length: 'passwordStrength.rules.length',
  capital: 'passwordStrength.rules.capital',
  number: 'passwordStrength.rules.number',
  symbol: 'passwordStrength.rules.symbol',
};

/** Which of the strong-password rules the current value meets, one row per rule. */
function RuleChecklist({ password }: { password: string }) {
  const { t } = useTranslation();

  return (
    <ul className={styles.rules} data-testid="password-rules">
      {(Object.keys(PASSWORD_RULES) as PasswordRule[]).map((rule) => {
        const met = PASSWORD_RULES[rule](password);
        return (
          <li
            key={rule}
            className={classNames(styles.rule, { [styles.ruleMet]: met })}
            data-met={met}
            data-testid={`password-rule-${rule}`}
          >
            <img alt="" className={styles.ruleIcon} src={met ? ruleMetIcon : ruleMissingIcon} />
            {t(RULE_LABEL_KEYS[rule])}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Four-segment meter for a new-password field plus a checklist of the strong-password rules. Renders
 * nothing until the player types, unless `showHint` asks for it up front — then the empty bar, label and
 * checklist already hold their space, so the form doesn't grow on the first keystroke.
 */
export function PasswordStrengthMeter({ password, showHint, className, 'data-testid': testId }: Props) {
  const { t } = useTranslation();

  if (!password && !showHint) {
    return null;
  }

  const strength = password ? getPasswordStrength(password) : null;
  const filled = strength ? PASSWORD_STRENGTH_LEVELS.indexOf(strength) + 1 : 0;

  return (
    <div
      aria-live="polite"
      className={classNames(styles.meter, { [styles[`meter--${strength}`]]: strength }, className)}
      data-testid={testId}
    >
      <div aria-hidden className={styles.bar} data-testid="password-strength-bar">
        {PASSWORD_STRENGTH_LEVELS.map((level, index) => (
          <span
            key={level}
            className={classNames(styles.segment, { [styles.filled]: index < filled })}
            data-testid={`password-strength-segment-${level}`}
          />
        ))}
      </div>
      <p className={styles.label} data-testid={strength ? 'password-strength' : undefined}>
        {/* Before the first keystroke the label shows without a level, so the layout doesn't shift. */}
        <Trans
          components={{ level: <b /> }}
          i18nKey="passwordStrength.level"
          values={{ level: strength ? t(STRENGTH_LABEL_KEYS[strength]) : '' }}
        />
      </p>
      <RuleChecklist password={password} />
    </div>
  );
}
